import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api.js';

// UC 34269 / UC 34270 — Inventory Alert Digest Settings

function typeBadge(type) {
  if (type === 'low_stock') return { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' };
  if (type === 'expiring_soon') return { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' };
  if (type === 'expired') return { background: '#1f2937', color: '#fff', border: 'none' };
  return { background: '#e5e7eb', color: '#374151', border: 'none' };
}

function typeLabel(type) {
  if (type === 'low_stock') return 'Low Stock';
  if (type === 'expiring_soon') return 'Expiring Soon';
  if (type === 'expired') return 'Expired';
  return type;
}

function priorityBadge(priority) {
  if (!priority || priority === 'low') return { background: '#d1fae5', color: '#065f46', label: 'Low' };
  if (priority === 'medium') return { background: '#fef3c7', color: '#92400e', label: 'Medium' };
  return { background: '#fee2e2', color: '#991b1b', label: 'High' };
}

export default function InventoryAlertDigestSettingsPage() {
  const navigate = useNavigate();

  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('digest');

  // Alert table filters
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterSearch, setFilterSearch] = useState('');

  // Preferences form state
  const [inAppEnabled, setInAppEnabled] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [alertTypes, setAlertTypes] = useState(['low_stock', 'expiring_soon']);
  const [lookahead, setLookahead] = useState(7);
  const [digestFreq, setDigestFreq] = useState('daily');
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefSaved, setPrefSaved] = useState(false);
  const [prefError, setPrefError] = useState('');

  const loadDigest = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/alert-digest');
      const data = res.data.data;
      setDigest(data);
      const p = data.preferences;
      setInAppEnabled(p.in_app_enabled ?? true);
      setEmailEnabled(p.email_enabled ?? false);
      setAlertTypes(p.alert_types || ['low_stock', 'expiring_soon']);
      setLookahead(p.expiry_lookahead_days || 7);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDigest(); }, [loadDigest]);

  function toggleAlertType(type) {
    setAlertTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  }

  async function savePreferences() {
    setPrefSaving(true);
    setPrefError('');
    setPrefSaved(false);
    try {
      await api.put('/alert-digest/preferences', {
        in_app_enabled: inAppEnabled,
        email_enabled: emailEnabled,
        alert_types: alertTypes,
        expiry_lookahead_days: lookahead,
      });
      setPrefSaved(true);
      await loadDigest();
    } catch (err) {
      setPrefError(err.response?.data?.error || err.message);
    } finally {
      setPrefSaving(false);
    }
  }

  const allAlerts = digest ? (digest.alerts || []) : [];

  const highPriorityCount = allAlerts.filter((a) =>
    a.priority === 'high' || a.type === 'expired'
  ).length;

  const deliveryLabel = [
    inAppEnabled ? 'In-App' : null,
    emailEnabled ? 'Email' : null,
  ].filter(Boolean).join(' + ') || 'None';

  const filteredAlerts = allAlerts.filter((a) => {
    if (filterType !== 'all' && a.type !== filterType) return false;
    if (filterPriority !== 'all') {
      const ap = a.priority || (a.type === 'expired' ? 'high' : 'low');
      if (ap !== filterPriority) return false;
    }
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      if (!(a.item_name || '').toLowerCase().includes(q) && !(a.item_code || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="container-fluid py-4" data-testid="alert-digest-page">

      {/* Header */}
      <div className="d-flex align-items-start justify-content-between mb-3 flex-wrap" style={{ gap: 12 }}>
        <div>
          <h2 className="h4 font-weight-bold mb-1">Inventory Alert Digest Settings</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Review today's low-stock and near-expiry alerts, then control how your daily digest is delivered.
          </p>
        </div>
        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          <span className="badge badge-light border px-3 py-2" style={{ fontSize: '0.8rem' }}>
            <i className="fa-solid fa-user-lock mr-1 text-muted" />Editable by current user only
          </span>
          <button
            type="button"
            className="btn btn-sm px-4 font-weight-bold"
            style={{ background: '#008080', color: '#fff', borderColor: '#008080' }}
            onClick={savePreferences}
            disabled={prefSaving}
            data-testid="btn-save-prefs"
          >
            {prefSaving ? 'Saving…' : 'Save Preferences'}
          </button>
        </div>
      </div>

      {prefSaved && (
        <div className="alert alert-success py-2 small mb-3" data-testid="pref-saved">
          <i className="fa-solid fa-circle-check mr-1" />Preferences saved successfully.
        </div>
      )}
      {prefError && (
        <div className="alert alert-danger py-2 small mb-3" data-testid="pref-error">{prefError}</div>
      )}
      {error && <div className="alert alert-danger mb-3" data-testid="digest-error">{error}</div>}

      {/* Warning banner */}
      <div className="alert mb-4 py-3" style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderLeft: '4px solid #f59e0b' }}>
        <div className="d-flex align-items-start">
          <i className="fa-solid fa-triangle-exclamation mr-2 mt-1" style={{ color: '#d97706' }} />
          <div>
            <strong style={{ color: '#92400e' }}>Daily review reminder:</strong>
            {' '}Digests are generated based on your alert type selection, priority filters, and configured look-ahead window.
            <div className="mt-1 small" style={{ color: '#92400e' }}>
              Late adjustments made after brand cut-off may appear in the next digest cycle.
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex align-items-center">
              <div className="rounded-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
                style={{ width: 52, height: 52, background: '#f3f4f6' }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ color: '#6b7280', fontSize: '1.3rem' }} />
              </div>
              <div>
                <div className="text-uppercase text-muted" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Today's Alerts</div>
                <div className="font-weight-bold" style={{ fontSize: '2rem', lineHeight: 1.1 }} data-testid="kpi-total-alerts">
                  {loading ? '—' : (digest?.total ?? 0)}
                </div>
                <small className="text-muted">Across low stock and near expiry conditions</small>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex align-items-center">
              <div className="rounded-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
                style={{ width: 52, height: 52, background: '#fff7ed' }}>
                <i className="fa-solid fa-fire-flame-curved" style={{ color: '#ea580c', fontSize: '1.3rem' }} />
              </div>
              <div>
                <div className="text-uppercase text-muted" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>High Priority</div>
                <div className="font-weight-bold" style={{ fontSize: '2rem', lineHeight: 1.1 }} data-testid="kpi-high-priority">
                  {loading ? '—' : highPriorityCount}
                </div>
                <small className="text-muted">Items requiring same-day attention</small>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex align-items-center">
              <div className="rounded-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
                style={{ width: 52, height: 52, background: '#f0fdf4' }}>
                <i className="fa-solid fa-inbox" style={{ color: '#16a34a', fontSize: '1.3rem' }} />
              </div>
              <div>
                <div className="text-uppercase text-muted" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Current Delivery</div>
                <div className="font-weight-bold" style={{ fontSize: '1.4rem', lineHeight: 1.2 }} data-testid="kpi-delivery">
                  {deliveryLabel}
                </div>
                <small className="text-muted">Daily at 7:00 AM, {lookahead}-day look-ahead</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs border-bottom mb-0" style={{ borderBottom: '2px solid #e5e7eb' }}>
        <li className="nav-item">
          <button
            className={`nav-link border-0 pb-3 font-weight-bold ${activeTab === 'digest' ? 'active' : 'text-muted'}`}
            style={activeTab === 'digest' ? { borderBottom: '2px solid #008080', color: '#008080', background: 'none' } : { background: 'none' }}
            onClick={() => setActiveTab('digest')}
            data-testid="tab-today-digest"
          >
            Today's Digest
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link border-0 pb-3 font-weight-bold ${activeTab === 'history' ? 'active' : 'text-muted'}`}
            style={activeTab === 'history' ? { borderBottom: '2px solid #008080', color: '#008080', background: 'none' } : { background: 'none' }}
            onClick={() => setActiveTab('history')}
            data-testid="tab-preferences-history"
          >
            Preferences History
          </button>
        </li>
      </ul>

      {activeTab === 'digest' && (
        <div className="row mt-4">
          {/* Preferences panel */}
          <div className="col-xl-4 col-lg-5 mb-4">
            <div className="card shadow-sm border-0" data-testid="preferences-panel">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="font-weight-bold mb-0">Digest Preference Settings</h6>
                  <span className="badge badge-light border" style={{ fontSize: '0.7rem' }}>Personal settings</span>
                </div>
                <p className="text-muted small mb-3">Each user can only update their own notification preferences.</p>

                {/* In-App toggle */}
                <div className="d-flex align-items-center justify-content-between py-3 border-bottom">
                  <div>
                    <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>In-App Digest Enabled</div>
                    <div className="text-muted small">Show alert digest summary inside the platform dashboard.</div>
                  </div>
                  <div className="custom-control custom-switch">
                    <input type="checkbox" className="custom-control-input" id="inAppEnabled"
                      checked={inAppEnabled} onChange={(e) => setInAppEnabled(e.target.checked)}
                      data-testid="pref-in-app" />
                    <label className="custom-control-label" htmlFor="inAppEnabled" />
                  </div>
                </div>

                {/* Email toggle */}
                <div className="d-flex align-items-center justify-content-between py-3 border-bottom">
                  <div>
                    <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>Email Digest Enabled</div>
                    <div className="text-muted small">Receive the alert digest through your registered email inbox.</div>
                  </div>
                  <div className="custom-control custom-switch">
                    <input type="checkbox" className="custom-control-input" id="emailEnabled"
                      checked={emailEnabled} onChange={(e) => setEmailEnabled(e.target.checked)}
                      data-testid="pref-email" />
                    <label className="custom-control-label" htmlFor="emailEnabled" />
                  </div>
                </div>

                {/* Digest Frequency */}
                <div className="py-3 border-bottom">
                  <div className="font-weight-bold mb-1" style={{ fontSize: '0.9rem' }}>Digest Frequency</div>
                  <select className="form-control form-control-sm" value={digestFreq}
                    onChange={(e) => setDigestFreq(e.target.value)} data-testid="pref-frequency">
                    <option value="daily">Daily (7:00 AM)</option>
                    <option value="twice-daily">Twice Daily (7 AM + 2 PM)</option>
                    <option value="weekly">Weekly Summary</option>
                  </select>
                </div>

                {/* Alert types */}
                <div className="py-3 border-bottom">
                  <div className="font-weight-bold mb-2" style={{ fontSize: '0.9rem' }}>Alert Types</div>
                  <div className="custom-control custom-checkbox mb-2">
                    <input type="checkbox" className="custom-control-input" id="typeLS"
                      checked={alertTypes.includes('low_stock')} onChange={() => toggleAlertType('low_stock')}
                      data-testid="pref-type-low-stock" />
                    <label className="custom-control-label" htmlFor="typeLS">Low Stock</label>
                  </div>
                  <div className="custom-control custom-checkbox">
                    <input type="checkbox" className="custom-control-input" id="typeES"
                      checked={alertTypes.includes('expiring_soon')} onChange={() => toggleAlertType('expiring_soon')}
                      data-testid="pref-type-expiring" />
                    <label className="custom-control-label" htmlFor="typeES">Expiring Soon</label>
                  </div>
                </div>

                {/* Look-ahead */}
                <div className="py-3">
                  <div className="font-weight-bold mb-1" style={{ fontSize: '0.9rem' }}>
                    Expiry Look-ahead Window
                  </div>
                  <div className="input-group input-group-sm">
                    <input type="number" className="form-control" min="1" max="30" value={lookahead}
                      onChange={(e) => setLookahead(Number(e.target.value))} data-testid="pref-lookahead" />
                    <div className="input-group-append">
                      <span className="input-group-text">days</span>
                    </div>
                  </div>
                  <small className="text-muted">Alerts show batches expiring within this window.</small>
                </div>
              </div>
            </div>
          </div>

          {/* Alert table panel */}
          <div className="col-xl-8 col-lg-7 mb-4">
            <div className="card shadow-sm border-0">
              <div className="card-body border-bottom pb-3">
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap" style={{ gap: 8 }}>
                  <div>
                    <h6 className="font-weight-bold mb-0">Today's Inventory Alerts</h6>
                    <small className="text-muted">15 rows per page with quick context access for inventory follow-up.</small>
                  </div>
                  <div className="text-muted small">
                    Digest generated: <strong>Today, 7:00 AM</strong>
                  </div>
                </div>
                <div className="row" style={{ gap: 0 }}>
                  <div className="col-sm-4 pr-2 mb-2">
                    <label className="small text-muted mb-1">Alert Type</label>
                    <select className="form-control form-control-sm" value={filterType}
                      onChange={(e) => setFilterType(e.target.value)} data-testid="filter-alert-type">
                      <option value="all">All Types</option>
                      <option value="low_stock">Low Stock</option>
                      <option value="expiring_soon">Expiring Soon</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                  <div className="col-sm-4 pr-2 mb-2">
                    <label className="small text-muted mb-1">Priority</label>
                    <select className="form-control form-control-sm" value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)} data-testid="filter-priority">
                      <option value="all">All Priorities</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div className="col-sm-4 mb-2">
                    <label className="small text-muted mb-1">Search Ingredient / SKU</label>
                    <div className="input-group input-group-sm">
                      <div className="input-group-prepend">
                        <span className="input-group-text bg-white"><i className="fa-solid fa-magnifying-glass text-muted" /></span>
                      </div>
                      <input type="text" className="form-control" placeholder="Search ingredient or SKU"
                        value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} data-testid="filter-search" />
                    </div>
                  </div>
                </div>
              </div>

              {loading && (
                <div className="text-center py-5" data-testid="digest-loading">
                  <div className="spinner-border" style={{ color: '#008080' }} role="status" />
                </div>
              )}

              {!loading && filteredAlerts.length === 0 && (
                <div className="alert alert-success m-3" data-testid="digest-empty">
                  <i className="fa-solid fa-circle-check mr-2" />No inventory alerts match the current filters. Stock levels and expiry dates are within thresholds.
                </div>
              )}

              {!loading && filteredAlerts.length > 0 && (
                <div className="table-responsive" data-testid="alert-table">
                  <table className="table table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                    <thead style={{ background: '#f8f9fa' }}>
                      <tr>
                        <th style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>Alert Type</th>
                        <th style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>Ingredient / SKU</th>
                        <th style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>Current Stock or Expiry Date</th>
                        <th style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>Threshold / Rule</th>
                        <th style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>Recommended Action</th>
                        <th style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAlerts.map((alert, idx) => {
                        const tb = typeBadge(alert.type);
                        const ap = alert.priority || (alert.type === 'expired' ? 'high' : 'low');
                        const pb = priorityBadge(ap);
                        return (
                          <tr key={`${alert.type}-${alert.item_id}-${idx}`} data-testid={`alert-row-${idx}`}>
                            <td>
                              <span style={{ ...tb, borderRadius: 4, padding: '3px 8px', fontSize: '0.72rem', fontWeight: 600, display: 'inline-block' }}
                                data-testid={`alert-type-${idx}`}>
                                {typeLabel(alert.type)}
                              </span>
                            </td>
                            <td data-testid={`alert-item-${idx}`}>
                              <div className="font-weight-bold">{alert.item_name}</div>
                              {alert.item_code && <small className="text-muted">{alert.item_code}</small>}
                            </td>
                            <td data-testid={`alert-qty-${idx}`}>
                              {alert.type === 'low_stock'
                                ? <>{alert.current_qty} {alert.unit} remaining</>
                                : alert.expiry_date || '—'}
                            </td>
                            <td data-testid={`alert-threshold-${idx}`}>
                              {alert.type === 'low_stock'
                                ? `Min par level ${alert.threshold} ${alert.unit}`
                                : `Within ${lookahead}-day window`}
                            </td>
                            <td className="small text-muted" data-testid={`alert-action-${idx}`}>
                              {alert.recommended_action}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => navigate(alert.inventory_context_url)}
                                data-testid={`btn-go-inventory-${idx}`}
                              >
                                Open<br />Inventory<br />Context
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="mt-4" data-testid="preferences-history-tab">
          <div className="card shadow-sm border-0">
            <div className="card-body text-center py-5 text-muted">
              <i className="fa-solid fa-clock-rotate-left fa-2x mb-3 d-block" style={{ color: '#d1d5db' }} />
              <div className="font-weight-bold">Preference history is not yet available.</div>
              <small>Changes to your digest settings will be logged here in a future release.</small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
